/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";

import Stack from "@mui/material/Stack";
import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Div from "@jumbo/shared/Div";
import Loader from "@jumbo/components/Loader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { getVariantList } from "app/redux/slice/variantSlice";

const GODOWN_OPTIONS = [
  { id: 1, name: "Vipulbhai" },
  { id: 2, name: "Dharmendra" },
  { id: 3, name: "Fenil" },
];

const AllProduct = () => {
  const dispatch = useDispatch();

  const variantData = useSelector((state) => state?.variant);

  const [searchText, setSearchText] = useState("");
  const [godownType, setGodownType] = useState(null);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("productId");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "variant" });

  const columns = [
    {
      id: "productId",
      label: "Product Name",
      sort: true,
      render: (elm) => elm?.product?.name,
    },
    { id: "name", label: "Variant Name", sort: true },
    { id: "quantity", label: "Qty", sort: true },
    { id: "godownType", label: "Godown Type" },
  ];

  const getData = () => {
    localStorage.setItem("variant-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(godownType && { godownType: godownType.name }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getVariantList(value));
  };

  const fetchTotalQuantity = async () => {
    try {
      const params = {
        isTotal: true,
        search: searchText.trim(),
        ...(godownType && { godownType: godownType.name }),
      };
      const response = await axios.get("variant/list", { params });
      setTotalQuantity(response?.data?.data?.totalQuantity || 0);
    } catch {
      setTotalQuantity(0);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
      fetchTotalQuantity();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF("l", "mm", "a4");

    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");

    // Subtitle
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Product Variant List", 14, 26);

    // Date & filter info
    doc.setFontSize(9);
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    let infoLine = `Generated on: ${dateStr}`;
    if (searchText.trim()) {
      infoLine += `  |  Search: "${searchText.trim()}"`;
    }
    if (godownType) {
      infoLine += `  |  Godown: ${godownType.name}`;
    }
    doc.text(infoLine, 14, 33);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Total Quantity: ${totalQuantity}`, 14, 40);
    doc.setFont("helvetica", "normal");

    // Table headers
    const tableHeaders = columns.map((col) => col.label);

    // Table body — use the same render logic as the columns
    const tableBody = (variantData?.list || []).map((row) =>
      columns.map((col) => {
        if (col.render) return col.render(row) ?? "";
        if (col.format && typeof row[col.id] === "number")
          return col.format(row[col.id]);
        return row[col.id] ?? "";
      }),
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableBody,
      startY: 46,
      theme: "grid",
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      bodyStyles: {
        fontSize: 10,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      styles: {
        cellPadding: 4,
        overflow: "linebreak",
      },
      margin: { top: 10, left: 14, right: 14 },
    });

    // File name with current date
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    doc.save(`product-variant-list-${yyyy}-${mm}-${dd}.pdf`);
  };

  useEffect(() => {
    getData();
    fetchTotalQuantity();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {variantData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      size="small"
                      options={GODOWN_OPTIONS}
                      getOptionLabel={(option) => option.name}
                      value={godownType}
                      onChange={(_, value) => setGodownType(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Godown Type" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                        fetchTotalQuantity();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                alignItems="center"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Product Variant List
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body1">
                    Total Qty: <strong>{totalQuantity}</strong>
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleDownloadPdf}
                  >
                    Download PDF
                  </Button>
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={variantData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={variantData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default AllProduct;
